import { AnimatePresence, motion } from "motion/react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BlockButton as UntypedBlockButton } from "../../inJoy-components/Button/BlockButton";
import { Icon } from "../../inJoy-components/Icon/Icon";
import { generateRandomTeamNames } from "./random-team-names";

const BlockButton = UntypedBlockButton as any;
const imgLogo = "/img/logo_word_color.svg";
const imgTeamLight = "/img/event-page/team_light.svg";
const imgTeamDark = "/img/event-page/team_dark.svg";

interface TeamSetupScreenProps {
	jerseyIsLight: boolean | null;
	onJerseyChange: (isLight: boolean) => void;
	teamNameLight: string;
	onTeamNameLightChange: (name: string) => void;
	teamNameDark: string;
	onTeamNameDarkChange: (name: string) => void;
	onConfirm: () => void;
	isSubmitting: boolean;
}

interface JerseyCardProps {
	active: boolean;
	icon: string;
	label: string;
	onClick: () => void;
}

const JerseyCard: React.FC<JerseyCardProps> = ({
	active,
	icon,
	label,
	onClick,
}) => (
	<button
		onClick={onClick}
		className={`flex-1 h-[205px] relative rounded-lg flex flex-col items-center justify-center gap-2 px-3 py-5 transition-colors ${active ? "bg-[#e6effc]" : ""}`}
	>
		<div
			className={`absolute inset-0 pointer-events-none rounded-lg border ${active ? "border-[#5494ee]" : "border-[#dfdfdf]"}`}
		/>
		{active && (
			<div className="absolute top-2 right-2 w-6 h-6">
				<Icon iconName="icon_checkCircle_fill" size="XLarge" color="#0060E5" />
			</div>
		)}
		<div className="w-[80px] h-[80px] rounded-full bg-white border border-white/20 shrink-0">
			<img alt={label} className="w-full h-full" src={icon} />
		</div>
		<p className="font-medium text-[16px] leading-[1.35] text-[#404040] text-center overflow-hidden text-ellipsis w-full">
			{label}
		</p>
	</button>
);

interface TeamNameCardProps {
	icon: string;
	label: string;
	value: string;
	onChange: (value: string) => void;
	placeholder: string;
}

const TeamNameCard: React.FC<TeamNameCardProps> = ({
	icon,
	label,
	value,
	onChange,
	placeholder,
}) => (
	<div className="bg-white relative rounded-lg w-full">
		<div className="absolute inset-0 pointer-events-none rounded-lg border border-[#dfdfdf]" />
		<div className="flex flex-col items-center gap-4 p-4">
			<div className="flex flex-col items-center">
				<div className="w-[72px] h-[72px] rounded-full shrink-0">
					<img alt={label} className="w-full h-full" src={icon} />
				</div>
				<p className="text-[14px] leading-[1.4] tracking-[0.25px] text-[#6d6d6d] text-center whitespace-nowrap">
					{label}
				</p>
			</div>
			<div className="w-full">
				<input
					type="text"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					placeholder={placeholder}
					className="w-full h-9 bg-[#f5f5f5] rounded px-2 py-2 text-[14px] leading-[1.4] tracking-[0.25px] text-[#404040] text-center outline-none placeholder:text-[#999]"
				/>
			</div>
		</div>
	</div>
);

export const TeamSetupScreen: React.FC<TeamSetupScreenProps> = ({
	jerseyIsLight,
	onJerseyChange,
	teamNameLight,
	onTeamNameLightChange,
	teamNameDark,
	onTeamNameDarkChange,
	onConfirm,
	isSubmitting,
}) => {
	const { t, i18n } = useTranslation();
	const [step, setStep] = useState<"jersey" | "naming">("jersey");

	const handleRandomize = () => {
		const [nameA, nameB] = generateRandomTeamNames(i18n.language);
		onTeamNameLightChange(nameA);
		onTeamNameDarkChange(nameB);
	};

	const canProceedToNaming = jerseyIsLight !== null;

	return (
		<div className="bg-white flex flex-col items-center min-h-screen pb-6 pt-5 px-4 relative w-full mx-auto overflow-hidden">
			{/* Logo */}
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.4 }}
				className="w-[109px] h-[24px] shrink-0"
			>
				<img
					alt="inJOY Logo"
					className="w-full h-full object-contain"
					src={imgLogo}
				/>
			</motion.div>

			{/* Center Content */}
			<div className="flex-1 w-full flex flex-col items-center justify-center">
				<AnimatePresence mode="wait">
					{step === "jersey" ? (
						<motion.div
							key="jersey"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -20 }}
							transition={{ duration: 0.3 }}
							className="flex flex-col items-center gap-10 py-5 w-full"
						>
							{/* Heading */}
							<div className="flex flex-col gap-3 text-center w-full">
								<p className="font-medium text-[28px] leading-[1.2] tracking-[-0.5px] text-[#404040]">
									{t("guest.team_setup.choose_jersey")}
								</p>
								<p className="text-[16px] leading-[1.4] tracking-[0.25px] text-[#6d6d6d]">
									{t("guest.team_setup.choose_jersey_desc")}
								</p>
							</div>

							{/* Jersey Cards */}
							<div className="flex gap-3 w-full">
								<JerseyCard
									active={jerseyIsLight === true}
									icon={imgTeamLight}
									label={t("guest.team_setup.light_jersey")}
									onClick={() => onJerseyChange(true)}
								/>
								<JerseyCard
									active={jerseyIsLight === false}
									icon={imgTeamDark}
									label={t("guest.team_setup.dark_jersey")}
									onClick={() => onJerseyChange(false)}
								/>
							</div>
						</motion.div>
					) : (
						<motion.div
							key="naming"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -20 }}
							transition={{ duration: 0.3 }}
							className="flex flex-col items-center gap-10 py-5 w-full"
						>
							{/* Heading */}
							<div className="flex flex-col gap-3 text-center w-full">
								<p className="font-medium text-[28px] leading-[1.2] tracking-[-0.5px] text-[#404040]">
									{t("guest.team_setup.name_teams")}
								</p>
								<p className="text-[16px] leading-[1.4] tracking-[0.25px] text-[#6d6d6d]">
									{t("guest.team_setup.name_teams_desc")}
								</p>
							</div>

							{/* Team Name Cards + Random Button */}
							<div className="flex flex-col gap-5 w-full">
								<TeamNameCard
									icon={imgTeamLight}
									label={t("guest.team_setup.light_jersey")}
									value={teamNameLight}
									onChange={onTeamNameLightChange}
									placeholder={t("guest.team_setup.light_team_placeholder")}
								/>
								<TeamNameCard
									icon={imgTeamDark}
									label={t("guest.team_setup.dark_jersey")}
									value={teamNameDark}
									onChange={onTeamNameDarkChange}
									placeholder={t("guest.team_setup.dark_team_placeholder")}
								/>
								<BlockButton
									type="Tertiary"
									icon="dice_five"
									size="Large"
									width="100%"
									onClick={handleRandomize}
								>
									{t("guest.team_setup.randomize")}
								</BlockButton>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>

			{/* Bottom Button */}
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.4, delay: 0.2 }}
				className="w-full shrink-0"
			>
				{step === "jersey" ? (
					<BlockButton
						type="Primary"
						size="Large"
						width="100%"
						disabled={!canProceedToNaming}
						onClick={() => setStep("naming")}
					>
						{t("guest.team_setup.continue")}
					</BlockButton>
				) : (
					<BlockButton
						type="Primary"
						size="Large"
						width="100%"
						loading={isSubmitting}
						disabled={isSubmitting}
						onClick={onConfirm}
					>
						{t("guest.team_setup.start_game")}
					</BlockButton>
				)}
			</motion.div>
		</div>
	);
};
